import type {
  SalesDashboardFilters,
  SubCustomerBucket,
  SubCustomerDailyPoint,
  SubCustomerOptions,
  SubCustomerRankItem,
  SubCustomerRequest,
  SubCustomerRequestInput,
  SubCustomerTop,
  SubCustomerTotals,
} from '../types'
import {
  NO_MATCHING_FACTORY_ID,
  SUB_CUSTOMER_DONUT_SLICES,
  SUB_CUSTOMER_FILTER_GROUPS,
  SUB_CUSTOMER_SEARCH_MIN_CHARS,
  SUB_CUSTOMER_T_KEYS as K,
} from '../constants'
import { resolveDateRange } from './index'
import { fillDateGaps, parseIsoDate } from './date-range'

/** Row id for the null-id "Unknown …" breakdown bucket (tables need a key). */
export const UNKNOWN_BUCKET_ID = 'unknown'
/** Row id for the folded donut tail. */
export const OTHER_BUCKET_ID = 'other'

export type SubCustomerFilterGroup = (typeof SUB_CUSTOMER_FILTER_GROUPS)[number]

/**
 * The header plant, read from the location selector only (the source of
 * X-Factory-Id in both portals). A location without a plant still scopes the
 * page — to nothing — exactly like the Sales dashboard.
 */
export function resolveGlobalFactoryIds(
  derivedFactoryId: string | null | undefined,
  locationId: string | null | undefined,
): string[] | undefined {
  if (derivedFactoryId) return [derivedFactoryId]
  if (locationId) return [NO_MATCHING_FACTORY_ID]
  return undefined
}

/**
 * Page filters + header scope -> API request. The plant is the header's only;
 * a header customer (sent as X-Tenant-Id by the http client) makes the page's
 * main-customer filter moot.
 */
export function buildSubCustomerRequest(input: SubCustomerRequestInput, subCustomerId?: string): SubCustomerRequest {
  const { from, to } = resolveDateRange(input.dateRange)
  return {
    from,
    to,
    factoryIds: input.globalFactoryIds,
    customerIds: input.globalTenantId ? undefined : input.filters.customerIds,
    cityIds: input.filters.cityIds,
    variantIds: input.filters.variantIds,
    subCustomerId,
  }
}

/** /options depends only on the window, the language and the header customer. */
export function toOptionsKey(request: Pick<SubCustomerRequest, 'from' | 'to'>, locale: string, tenantId?: string | null): string {
  return [request.from, request.to, locale, tenantId ?? ''].join('|')
}

/** Apply a filter change; an emptied list is dropped so "no filter" has one shape. */
export function mergeFilters(filters: SalesDashboardFilters, patch: Partial<SalesDashboardFilters>): SalesDashboardFilters {
  const merged = { ...filters, ...patch }
  return Object.fromEntries(Object.entries(merged).filter(([, ids]) => ids && ids.length > 0)) as SalesDashboardFilters
}

/** How many values are picked across the page filters (the drawer button badge). */
export function countActiveFilters(filters: SalesDashboardFilters): number {
  return SUB_CUSTOMER_FILTER_GROUPS.reduce((sum, group) => sum + (filters[group]?.length ?? 0), 0)
}

const OPTION_LIST: Record<SubCustomerFilterGroup, keyof SubCustomerOptions> = {
  customerIds: 'customers',
  cityIds: 'cities',
  variantIds: 'cementTypes',
}

/** One removable chip per picked value, named from the option lists (id if the list no longer has it). */
export function buildFilterChips(filters: SalesDashboardFilters, options: SubCustomerOptions) {
  return SUB_CUSTOMER_FILTER_GROUPS.flatMap((group) =>
    (filters[group] ?? []).map((id) => ({
      group,
      id,
      label: options[OPTION_LIST[group]].find((option) => option.id === id)?.name ?? id,
    })),
  )
}

/** Each row's share of the scope's total tons — the % shown and the bar length. */
export function rankRows<T extends SubCustomerTotals>(rows: T[], totalTons: number): Array<T & { share: number }> {
  return rows.map((row) => ({ ...row, share: totalTons > 0 ? (row.tons / totalTons) * 100 : 0 }))
}

const TONS_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })
const COUNT_FORMAT = new Intl.NumberFormat('en-US')

/** Exact tons with separators ("12,345.6") — tables and KPIs; `formatTons` stays the compact chart form. */
export function formatTonsExact(value: number): string {
  return TONS_FORMAT.format(value)
}

export function formatCount(value: number): string {
  return COUNT_FORMAT.format(value)
}

export function formatShare(value: number): string {
  return `${value.toFixed(1)}%`
}

export function withRank<T>(rows: T[]): Array<T & { rank: number }> {
  return rows.map((row, index) => ({ ...row, rank: index + 1 }))
}

/** Top-20 rows: the line under the name is the main customer (admin) or the code (customer portal). */
export function toRankingRows(items: SubCustomerRankItem[] | undefined, showMainCustomer: boolean): Array<SubCustomerRankItem & { subtitle: string | null }> {
  return (items ?? []).map((item) => ({ ...item, subtitle: showMainCustomer ? item.customerName : item.code }))
}

export function toBucketRows(buckets: SubCustomerBucket[], unknownLabel: string) {
  return buckets.map((bucket) => ({
    id: bucket.id ?? UNKNOWN_BUCKET_ID,
    name: bucket.name ?? unknownLabel,
    orders: bucket.orders,
    tons: bucket.tons,
  }))
}

/** Donut slices: the largest few, then one "Other" slice that sums the rest (tons and orders). */
export function toDonutRows(buckets: SubCustomerBucket[], unknownLabel: string, otherLabel: string, slices = SUB_CUSTOMER_DONUT_SLICES) {
  const rows = toBucketRows(buckets, unknownLabel)
  if (rows.length <= slices + 1) return rows
  const tail = rows.slice(slices)
  const other = tail.reduce((sum, row) => ({ ...sum, orders: sum.orders + row.orders, tons: sum.tons + row.tons }), {
    id: OTHER_BUCKET_ID, name: otherLabel, orders: 0, tons: 0,
  })
  return [...rows.slice(0, slices), other]
}

/** Daily trend: one point per day of the window, labelled in the viewer's locale. */
export function toTrendRows(rows: SubCustomerDailyPoint[], from: string, to: string, locale: string) {
  return fillDateGaps(rows, from, to, (date) => ({ date, orders: 0, tons: 0 })).map((row) => ({
    ...row,
    label: parseIsoDate(row.date).toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
  }))
}

/** Average delivered tons per order across the ranked sub-customers. */
export function avgTonsPerOrder(top: SubCustomerTop | null): number {
  const totals = top?.totals
  return totals && totals.orders > 0 ? totals.tons / totals.orders : 0
}

/** What the search dropdown says instead of results, if anything. */
export function searchStatusKey(query: string, loading: boolean, resultCount: number): string | null {
  if (query.trim().length < SUB_CUSTOMER_SEARCH_MIN_CHARS) return K.SEARCH.MIN_CHARS
  if (loading) return K.SEARCH.SEARCHING
  if (resultCount === 0) return K.SEARCH.NO_RESULTS
  return null
}
